import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { checkEligibility } from 'api/insurance/checkEligibility';
import { updateEligibilityToNoCheckStatus } from 'api/insurance/updateEligibilityToNoCheckStatus';
import { getAccountLeads } from 'api/leads/leads';
import { FormProvider } from 'components/core/form';
import { InsuranceFormModel } from 'components/insurance/InsuranceForm';
import { useConsult } from 'contexts/ConsultContext';
import { useCurrentUser } from 'contexts/CurrentUserContext';
import { useSponsors } from 'contexts/SponsorsContext';
import axiosInstance from 'helpers/axiosInstance';
import { useNpReferral } from 'hooks/useNpReferral';
import { useNpReferralOnly } from 'hooks/useNpReferralOnly';
import isEqual from 'lodash/isEqual';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useNextStep } from '../../../api/account/useNextStep';
import { FeatureFlags, useFlag } from '../../../hooks/useFlag';
import { CommonOnboardingPage } from '../constants';
import {
  EligibilityCheckFailedModal,
  EligibilityCheckNotWorkingModal,
} from './eligibility/EligibilityCheckFailedModal';
import { EligibilityErrorType } from './eligibility/EligibilityError';
import { EligibilityForm } from './eligibility/EligibilityForm';
import { EligibilityHelpPage } from './eligibility/EligibilityHelpPage';
import { EligibilitySuccessModal } from './eligibility/EligibilitySuccessModal';
import { Content } from './Styles';

enum EligibilityFlow {
  ShowForm,
  ShowSuccess,
  ShowHelpPage,
  ShowError,
  ShowOtherUploadPage,
}

const MAX_WAIT_TIME = 1000 * 60;

interface EligibilityCheckPageProps {
  selfNpReferFlow?: boolean;
  consultId?: string;
  settingsFlow?: boolean;
}

const EligibilityCheckPage: FC<EligibilityCheckPageProps> = ({
  selfNpReferFlow = false,
  consultId,
  settingsFlow = false,
}) => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { consult, refresh: refreshConsult } = useConsult();
  const {
    state: { account },
    refresh: refreshAccount,
  } = useCurrentUser();
  const nextStep = useNextStep();
  const { program } = useParams();
  const navigateEligibilityCheck = useFlag(FeatureFlags.NavigateEligibilityCheck);
  const isNpReferralOnly = useNpReferralOnly();
  const isNpReferralFlow = useNpReferral() || isNpReferralOnly;

  /**
   * Creating an eligiblity check updates fields on both the
   * consult and the parent account (ToT verified)
   *
   * Regardless of what action the user takes, we need to
   * refresh both prior to sending them downstream
   */
  const refresh = useCallback(async () => {
    await Promise.all([refreshConsult?.(), refreshAccount?.()]);
  }, [refreshAccount, refreshConsult]);

  const [currentFlowStep, setCurrentFlowStep] = useState(EligibilityFlow.ShowForm);
  const [eligibilityError, setEligibilityError] = useState<EligibilityErrorType | undefined>();
  const [lastSubmittedValues, setLastSubmittedValues] = useState<InsuranceFormModel | undefined>();

  const [allowBackButton, setAllowBackButton] = useState(true);

  const [failureCount, setFailureCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const timer = useRef(0);
  const settingUrl = consult?.patientId ? `/settings/${consult.patientId}` : '/home';

  useEffect(() => {
    const leads = getAccountLeads();
    leads.then((data) => {
      const ccLead = data.find((lead) => lead.leadType === 'CC' || lead.leadType === 'NP');
      if (ccLead) {
        const {
          insuranceMemberId,
          insuranceGroupId,
          insurancePayerId,
          insurancePhoneNumber,
          insuranceCardBackUrl,
          insuranceCardFrontUrl,
          id,
        } = ccLead;
        setLastSubmittedValues({
          leadId: id,
          memberId: insuranceMemberId ?? '',
          groupNumber: insuranceGroupId ?? '',
          payerId: insurancePayerId ?? '',
          phoneNumber: insurancePhoneNumber ?? '',
          insuranceCardFrontUrl: insuranceCardFrontUrl ?? '',
          insuranceCardBackUrl: insuranceCardBackUrl ?? '',
        });
      }
    });
  }, []);
  const { isCurrentAccountSponsorOther } = useSponsors();
  useEffect(() => {
    if (isCurrentAccountSponsorOther) {
      setCurrentFlowStep(EligibilityFlow.ShowOtherUploadPage);
    }

    if (isCurrentAccountSponsorOther !== undefined) {
      setIsLoading(false);
    }
  }, [isCurrentAccountSponsorOther]);

  const doNotCheckEligibility = async () => {
    setIsLoading(true);
    try {
      await updateEligibilityToNoCheckStatus(params['consultId'] ?? '');
      await refresh?.();
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
    if (location?.state?.to) {
      return navigate(location.state.to);
    }
    if (selfNpReferFlow) {
      return navigate(`/np/members/${consult?.patientId}/agreements`);
    }
    if (isNpReferralOnly) {
      return navigate(`/member/${consult?.patientId}/agreements/${params['consultId']}`);
    }

    navigate(`/consult/${params['consultId'] ?? ''}/start`);
  };

  const handleFormSubmit = useCallback(
    async (data: InsuranceFormModel) => {
      if (data.sponsorId === 'None') {
        doNotCheckEligibility();
        return;
      }
      const startTime = Date.now();

      const areValuesEqual = isEqual(data, lastSubmittedValues);

      if (!areValuesEqual) {
        setFailureCount(0);
      }

      setLastSubmittedValues(data);

      const info = {
        sponsor_id: data.sponsorId,
        member_id: data.memberId,
        group_number: data.groupNumber,
        payer_id: data.payerId,
        insurance_phone: data.phoneNumber,
        member_first_name: data.memberFirstName,
        member_last_name: data.memberLastName,
        member_dob: data.memberDob,
        front_card: data.insuranceCardFront,
        back_card: data.insuranceCardBack,
        lead_id: data.leadId,
      };

      setIsLoading(true);

      const insuranceResponse = await checkEligibility(info, (params['consultId'] || consultId) as string);

      if (insuranceResponse === 'pending') {
        setIsLoading(false);
        showHelpPage(false);
        return;
      }

      // eligible and in_progress are the only valid responses
      if (insuranceResponse !== 'eligible' && insuranceResponse !== 'in_progress') {
        // check the type of error and set the error type
        setEligibilityError(EligibilityErrorType.Generic);
        setFailureCount(failureCount + 1);
        setIsLoading(false);
        return;
      } else if (insuranceResponse == 'eligible') {
        try {
          await refresh?.();
        } catch (e) {
          console.error(e);
        } finally {
          setCurrentFlowStep(EligibilityFlow.ShowSuccess);
          setIsLoading(false);
        }
      } else if (insuranceResponse == 'in_progress') {
        clearTimeout(timer.current);

        // if we have exceeded the max wait time, show generic error
        if (Date.now() - startTime > MAX_WAIT_TIME) {
          setEligibilityError(EligibilityErrorType.Generic);
          setCurrentFlowStep(EligibilityFlow.ShowError);
          return;
        }

        // call handle submit again after 2 seconds
        timer.current = window.setTimeout(() => handleFormSubmit(data), 2000);
      }
    },
    [failureCount, lastSubmittedValues, params, refresh],
  );

  const showHelpPage = (allowBack = true) => {
    setCurrentFlowStep(EligibilityFlow.ShowHelpPage);
    setAllowBackButton(allowBack);
    setEligibilityError(undefined);
  };

  const skipUpload = async () => {
    await refresh?.();

    completeHelpStep();
  };

  const submitUpload = async (front: File, back: File) => {
    const data = new FormData();
    data.append(
      'family_insurance[card_info]',
      JSON.stringify(
        lastSubmittedValues
          ? {
              insurance_carrier: account?.insuranceCarrier,
              ...lastSubmittedValues,
            }
          : {},
      ),
    );
    data.append('family_insurance[card_images][front_card]', front);
    data.append('family_insurance[card_images][back_card]', back);
    await axiosInstance.post(`/patients/patients/${consult?.patient.id}/family_insurance`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    await refresh?.();

    completeHelpStep();
  };

  const submitOtherUpload = async (front: File, back: File) => {
    setIsLoading(true);
    // 'Other' does not currently have its eligibility checked
    // by Apero (or any other vendor), but we need an eligibility
    // check record to be present, and we might end up checking
    // 'Other' eligibility in the future. This creates an empty
    // eligibility check. It will be pending for review.
    const info = {
      member_id: '',
      group_number: '',
      payer_id: '',
      insurance_phone: '',
      member_first_name: '',
      member_last_name: '',
      member_dob: '',
    };
    await checkEligibility(info, params['consultId'] as string);
    submitUpload(front, back);
    setIsLoading(false);
  };

  const completeHelpStep = async () => {
    if (navigateEligibilityCheck) {
      return nextStep
        .mutateAsync({
          page: CommonOnboardingPage.EligibilityCheck,
          patientConsultId: params['consultId'],
          patientId: consult?.patientId,
          program,
        })
        .then((r) => {
          navigate(r.url);
        });
    }

    if (selfNpReferFlow) {
      navigate(`/np/members/${consult?.patientId}/agreements`);
      return;
    }
    if (isNpReferralFlow) {
      navigate(`/member/${consult?.patientId}/agreements/${params['consultId']}`);
      return;
    }
    if (location?.state?.to) {
      navigate(location.state.to);
      return;
    }
    if (!consult) return;

    navigate(`/consult/${params['consultId']}/start`);
  };

  return (
    <Content>
      {currentFlowStep === EligibilityFlow.ShowForm && (
        <FormProvider validateOnBlur controlRef={formRef}>
          <EligibilityForm
            loading={isLoading}
            errorCount={failureCount}
            handleSubmit={handleFormSubmit}
            abandonEligibilityCheck={doNotCheckEligibility}
            initialValues={lastSubmittedValues}
            formRef={formRef}
          />
        </FormProvider>
      )}
      {currentFlowStep !== EligibilityFlow.ShowError && eligibilityError && failureCount <= 1 && (
        <EligibilityCheckFailedModal
          setEligibilityError={setEligibilityError}
          doNotCheckEligibility={doNotCheckEligibility}
          showHelpPage={showHelpPage}
        />
      )}
      {currentFlowStep !== EligibilityFlow.ShowError && eligibilityError && failureCount >= 2 && (
        <EligibilityCheckNotWorkingModal
          setEligibilityError={setEligibilityError}
          doNotCheckEligibility={doNotCheckEligibility}
          showHelpPage={showHelpPage}
        />
      )}
      {currentFlowStep === EligibilityFlow.ShowSuccess && (
        <EligibilitySuccessModal
          to={settingsFlow ? settingUrl : undefined}
          selfNpReferFlow={selfNpReferFlow}
          isNpReferralOnly={isNpReferralFlow}
        />
      )}
      {currentFlowStep === EligibilityFlow.ShowHelpPage && (
        <EligibilityHelpPage
          prohibitBackButton={!allowBackButton}
          onGoBack={() => setCurrentFlowStep(EligibilityFlow.ShowForm)}
          onSkip={skipUpload}
          onSubmit={submitUpload}
        />
      )}
      {currentFlowStep === EligibilityFlow.ShowOtherUploadPage && (
        <EligibilityHelpPage
          prohibitBackButton={true}
          prohibitSkipButton={true}
          onGoBack={() => {}}
          onSkip={() => {}}
          onSubmit={submitOtherUpload}
        />
      )}
    </Content>
  );
};

export default EligibilityCheckPage;
